// Read-only helpers over the CANONICAL Creative Document (no second model).
// Resolution of a format variant always comes from the Creative Engine
// (`resolveVariant`, the same function the compiler uses), so the canvas
// shows exactly what the Delivery Artifact will carry.

import {
  resolveVariant,
  type Action,
  type ArtifactLayoutV1,
  type AuthoringState,
  type CreativeDocument,
  type Element,
  type Frame,
  type Layout,
  type LayoutOverride,
  type Scene,
  type TextContent,
} from "@interactads/creative-model";

export type ElementPresentation = ArtifactLayoutV1["elements"][string];

/** The concept master (layouts[0]): base element values are in its coordinates (CREATIVE-MODEL §4.2). */
export const masterLayout = (doc: CreativeDocument): Layout => doc.layouts[0] as Layout;

export const findLayout = (doc: CreativeDocument, id: string) => doc.layouts.find((l) => l.id === id);

export function layoutSize(layout: Layout): { width: number; height: number } {
  return layout.sizing.mode === "fixed"
    ? { width: layout.sizing.width, height: layout.sizing.height }
    : { width: layout.sizing.design.width, height: layout.sizing.design.height };
}

export const layoutLabel = (layout: Layout) => {
  const { width, height } = layoutSize(layout);
  const key = layout.formatKeys[0] ?? "";
  return key === `${String(width)}x${String(height)}` ? key : `${key} · ${String(width)}×${String(height)}`;
};

/** The scene being edited (ephemeral editor choice); defaults to the initial scene. */
export const editingScene = (doc: CreativeDocument, sceneId?: string): Scene =>
  (doc.scenes.find((s) => s.id === sceneId) ??
    doc.scenes.find((s) => s.id === doc.initialSceneId) ??
    doc.scenes[0]) as Scene;

export const findScene = (doc: CreativeDocument, id: string) => doc.scenes.find((s) => s.id === id);

/** Scene whose top-level layers contain the element (any variant), if any. */
export function sceneOfElement(doc: CreativeDocument, elementId: string): Scene | undefined {
  return doc.scenes.find(
    (s) => s.children.includes(elementId) || Object.values(s.childrenByLayout ?? {}).some((l) => l.includes(elementId))
  );
}

export const sceneLabel = (doc: CreativeDocument, scene: Scene) =>
  scene.name ?? `Escena ${String(doc.scenes.indexOf(scene) + 1)}`;

export const findElement = (doc: CreativeDocument, id: string): Element | undefined =>
  doc.elements.find((e) => e.id === id);

/** Whether an element exists in a variant (onlyIn); scene children only (7.6C edits top-level layers). */
export function presentIn(el: Element, layoutId: string): boolean {
  return el.onlyIn === undefined || el.onlyIn.includes(layoutId);
}

/** Variants an element exists in, in document order. */
export const layoutsOf = (doc: CreativeDocument, el: Element) =>
  doc.layouts.filter((l) => presentIn(el, l.id)).map((l) => l.id);

/**
 * The "home" variant of an element: where its BASE values are expressed —
 * the master if the element exists there, else the first variant it exists
 * in. Edits in the home variant write base values; edits elsewhere write
 * that variant's override.
 */
export function homeLayout(doc: CreativeDocument, el: Element): string | undefined {
  return layoutsOf(doc, el)[0];
}

/** Back-to-front order of the layers of a scene in a variant (canonical z-order). */
export function layerOrder(doc: CreativeDocument, layoutId: string, sceneId?: string): string[] {
  const scene = editingScene(doc, sceneId);
  const order = scene.childrenByLayout?.[layoutId] ?? scene.children;
  return order.filter((id) => {
    const el = findElement(doc, id);
    return el !== undefined && presentIn(el, layoutId);
  });
}

export interface ElementView {
  readonly element: Element;
  /** Resolved presentation in the current variant (from the Creative Engine). */
  readonly presentation: ElementPresentation;
  readonly frame: Frame;
}

/** Resolved, back-to-front views of a scene's layers in a variant. */
export function variantViews(doc: CreativeDocument, layoutId: string, sceneId?: string): ElementView[] {
  const resolved = resolveVariant(doc, layoutId);
  if (!resolved) return [];
  const scene = editingScene(doc, sceneId);
  const ids = resolved.scenes[scene.id] ?? [];
  return ids.flatMap((id) => {
    const element = findElement(doc, id);
    const presentation = resolved.elements[id];
    return element && presentation ? [{ element, presentation, frame: presentation.frame as unknown as Frame }] : [];
  });
}

export const overrideOf = (doc: CreativeDocument, layoutId: string, elementId: string): LayoutOverride | undefined =>
  findLayout(doc, layoutId)?.overrides?.[elementId];

/** Next free document-scoped id with a prefix ("text1", "text2", …). Ids are document-local, not control-plane ids. */
export function nextId(taken: Iterable<string>, prefix: string): string {
  const used = new Set(taken);
  for (let n = 1; ; n++) {
    const candidate = `${prefix}${String(n)}`;
    if (!used.has(candidate)) return candidate;
  }
}

export const elementIds = (doc: CreativeDocument) => doc.elements.map((e) => e.id);

export const TYPE_NAME: Record<Element["type"], string> = {
  text: "Texto",
  image: "Imagen",
  shape: "Forma",
  button: "Botón",
  video: "Video",
  audio: "Audio",
  group: "Grupo",
  hotspot: "Zona clicable",
  field: "Campo",
  component: "Componente",
};

/** Layer name shown to the user (canonical `name`, else its kind). */
export const elementLabel = (el: Element) => el.name ?? TYPE_NAME[el.type];

export const EMPTY_AUTHORING: AuthoringState = { schemaVersion: 1 };

export const isLocked = (a: AuthoringState, id: string) => a.locked?.includes(id) ?? false;
export const isEditorHidden = (a: AuthoringState, id: string) => a.editorHidden?.includes(id) ?? false;

/**
 * "Simple" text the Studio edits directly: text runs without bindings whose only
 * style is a uniform underline (canonical run style, CREATIVE-MODEL §5.3).
 * Anything richer returns null and is kept untouched ("formato avanzado").
 */
export function simpleText(content: {
  readonly runs: readonly unknown[];
}): { readonly text: string; readonly underline: boolean } | null {
  let text = "";
  let underline: boolean | undefined;
  for (const run of content.runs) {
    if (typeof run !== "object" || run === null || !("text" in run)) return null;
    const style = (run as { style?: Record<string, unknown> }).style;
    const keys = style ? Object.keys(style) : [];
    if (keys.some((k) => k !== "underline")) return null;
    const u = style?.underline === true;
    if (underline !== undefined && u !== underline) return null;
    underline = u;
    text += (run as { text: string }).text;
  }
  return { text, underline: underline ?? false };
}

/** Canonical content for simple text (one run; underline as its run style). */
export const simpleContent = (text: string, underline: boolean): TextContent => ({
  runs: [underline ? { text, style: { underline: true } } : { text }],
});

/** Plain text of simple content (inline editing), else null. */
export function plainText(content: { readonly runs: readonly unknown[] }): string | null {
  return simpleText(content)?.text ?? null;
}

export const textOf = (content: { readonly runs: readonly unknown[] }) =>
  content.runs
    .map((r) => (typeof r === "object" && r !== null && "text" in r ? (r as { text: string }).text : "0"))
    .join("");

/** Element types the Studio authors; others render as placeholders and keep their data untouched. */
export const AUTHORED_TYPES = ["text", "image", "shape", "button", "hotspot"] as const;
export type AuthoredType = (typeof AUTHORED_TYPES)[number];
export const isAuthored = (el: Element): el is Extract<Element, { type: AuthoredType }> =>
  (AUTHORED_TYPES as readonly string[]).includes(el.type);

/** The landing link used by default by CTAs and by the fallback ("destino principal"). */
export const MAIN_LINK_ID = "landing";
export const tapRuleId = (elementId: string) => `tap_${elementId}`;
export const ownLinkId = (elementId: string) => `link_${elementId}`;

/** Link a CTA/hotspot opens: the link.open of its tap rule (pointer.tap on the element), if any. */
export function linkOfElement(doc: CreativeDocument, elementId: string): string | undefined {
  const rules = (doc.rules ?? []).filter((r) => r.on.type === "pointer.tap" && r.on.target === elementId);
  // The CTA rule (`tap_<id>`) first, then any other tap rule of the element.
  rules.sort((a, b) => Number(b.id === tapRuleId(elementId)) - Number(a.id === tapRuleId(elementId)));
  for (const rule of rules) {
    const action = rule.do.find((a): a is Extract<Action, { type: "link.open" }> => a.type === "link.open");
    if (action) return action.link;
  }
  return undefined;
}
